/**
 * FASE 2 — DATA DA GRADUAÇÃO ("faixa atual desde").
 *
 * É só uma data, OPCIONAL. O sistema NÃO guarda grau (varia demais de equipe
 * para equipe) e NÃO decide quem está pronto para graduar — quando muito, mostra
 * há quanto tempo o atleta está na faixa, e quem decide é a equipe.
 *
 * Espelha `backend/server.py` (clean_data_iso / belt_since_warnings): uma regra
 * só, com os mesmos avisos dos dois lados.
 */

/** A data de hoje em ISO (YYYY-MM-DD), no fuso local. */
export function hojeIso() {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/** ISO → "dd/mm/aaaa" (para mostrar na tela). */
export function formatarDataBr(iso) {
  const texto = String(iso ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return '';
  return `${texto.slice(8, 10)}/${texto.slice(5, 7)}/${texto.slice(0, 4)}`;
}

/** ISO → Date local (meia-noite), sem o deslocamento de fuso do `new Date(iso)`. */
function paraData(iso) {
  const texto = String(iso ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return null;
  const [ano, mes, dia] = texto.split('-').map(Number);
  const data = new Date(ano, mes - 1, dia);
  if (Number.isNaN(data.getTime())) return null;
  // O JS "rola" data impossível (31/02 vira 02/03): confere se voltou a mesma.
  // Assim o frontend recusa exatamente o que o `clean_data_iso` do servidor recusa.
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
    return null;
  }
  return data;
}

/** Data ISO válida? (mesma ideia do `clean_data_iso` do servidor) */
export function dataIsoValida(iso) {
  return paraData(iso) !== null;
}

/**
 * Há quanto tempo o atleta está na faixa, em texto: "2 anos e 3 meses",
 * "5 meses", "23 dias". Devolve null quando não há data (ou é futura).
 */
export function tempoNaFaixa(desde, hoje = hojeIso()) {
  const inicio = paraData(desde);
  const fim = paraData(hoje);
  if (!inicio || !fim || inicio > fim) return null;

  let meses = (fim.getFullYear() - inicio.getFullYear()) * 12
    + (fim.getMonth() - inicio.getMonth());
  if (fim.getDate() < inicio.getDate()) meses -= 1;

  const anos = Math.floor(meses / 12);
  const restoMeses = meses % 12;

  if (anos >= 1) {
    const parteAnos = anos === 1 ? '1 ano' : `${anos} anos`;
    if (restoMeses === 0) return parteAnos;
    const parteMeses = restoMeses === 1 ? '1 mês' : `${restoMeses} meses`;
    return `${parteAnos} e ${parteMeses}`;
  }
  if (meses >= 1) return meses === 1 ? '1 mês' : `${meses} meses`;

  const dias = Math.round((fim - inicio) / 86400000);
  if (dias <= 0) return 'menos de 1 dia';
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

/**
 * Avisos de coerência da data da graduação (os MESMOS do servidor).
 * Devolve uma lista de textos — nada aqui bloqueia o salvamento.
 */
export function avisosDataGraduacao(desde, { startDate, birthDate, hoje = hojeIso() } = {}) {
  const avisos = [];
  const data = String(desde ?? '').slice(0, 10);
  if (!dataIsoValida(data)) return avisos;

  if (data > hoje) {
    avisos.push(`Data da graduação no futuro (${formatarDataBr(data)}) — confira.`);
  }
  if (dataIsoValida(startDate) && data < String(startDate).slice(0, 10)) {
    avisos.push(
      `Data da graduação anterior ao início no Jiu-Jitsu (${formatarDataBr(startDate)}) — confira.`,
    );
  }
  if (dataIsoValida(birthDate) && data < String(birthDate).slice(0, 10)) {
    avisos.push('Data da graduação anterior ao nascimento do atleta — confira.');
  }
  return avisos;
}

/**
 * Frase do campo: junta o aviso (quando existe) com o tempo na faixa.
 * Ex.: "Nesta faixa há 2 anos e 3 meses." / "Data no futuro (01/01/2030) — confira."
 */
export function resumoDataGraduacao(desde, opcoes = {}) {
  const avisos = avisosDataGraduacao(desde, opcoes);
  if (avisos.length) return { tipo: 'aviso', texto: avisos[0] };
  const tempo = tempoNaFaixa(desde, opcoes.hoje);
  if (!tempo) return { tipo: 'vazio', texto: 'Sem data — o sistema não calcula tempo na faixa.' };
  return { tipo: 'ok', texto: `Nesta faixa há ${tempo}.` };
}

// ---------------------------------------------------------------------------
// FASE 3 — HISTÓRICO DE GRADUAÇÃO (a linha do tempo) e o filtro da listagem
// ---------------------------------------------------------------------------

/**
 * Quantos meses se passaram desde uma data (null quando não há data válida ou
 * quando ela está no futuro). É o número usado pelo filtro "sem graduar há X".
 */
export function mesesDesde(iso, hoje = hojeIso()) {
  const inicio = paraData(iso);
  const fim = paraData(hoje);
  if (!inicio || !fim || inicio > fim) return null;
  let meses = (fim.getFullYear() - inicio.getFullYear()) * 12
    + (fim.getMonth() - inicio.getMonth());
  if (fim.getDate() < inicio.getDate()) meses -= 1;
  return Math.max(meses, 0);
}

/**
 * O atleta entra no filtro "sem graduar há X meses"?
 *   * `meses = 0`        → qualquer um (não filtra);
 *   * atleta SEM data    → entra (é justamente quem ninguém sabe quando graduou);
 *   * com data           → entra quando passou do limite.
 */
export function semGraduarHa(iso, meses, hoje = hojeIso()) {
  if (!meses) return true;
  const passados = mesesDesde(iso, hoje);
  if (passados === null) return true;   // sem data (ou data futura) → precisa de atenção
  return passados >= meses;
}

/** Ordena o histórico do mais recente para o mais antigo (a linha do tempo). */
export function ordenarGraduacoes(registros = []) {
  return [...registros].sort((a, b) => {
    const da = String(a?.date || '');
    const db = String(b?.date || '');
    if (da !== db) return db.localeCompare(da);
    return String(b?.created_at || '').localeCompare(String(a?.created_at || ''));
  });
}

/** Opções do filtro "sem graduar há" (o limite é escolhido na hora, não é regra). */
export const FILTROS_SEM_GRADUAR = [
  { value: 0, label: 'Graduação: qualquer' },
  { value: 6, label: 'Sem graduar há 6 meses' },
  { value: 12, label: 'Sem graduar há 1 ano' },
  { value: 24, label: 'Sem graduar há 2 anos' },
];
